import React from "react";

import { mdiArrowRight } from "@mdi/js";
import Icon from "@mdi/react";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useTranslation } from "react-i18next";

import AdbreakBlock from "./AdbreakBlock";
import AdbreakTimeline from "./AdbreakTimeline";
import VideoOverview from "./VideoOverview";
import MediaAPI from "../../../api/MediaAPI";
import { AD_BREAK } from "../../../constants/media";
import useFAST from "../../../contexts/FASTContext";
import SidepanelBlock from "../../block/SidepanelBlock";
import Input from "../../form/Input";
import FastFormatTag from "../../tag/FastFormatTag";
import FastScheduleTag from "../../tag/FastScheduleTag";
import Tag from "../../tag/Tag";

import type { JSX } from "react";

import type { MediaFormatWithAdBreak } from "../../../constants/media";
import type { InterstitialAdbreaks } from "../timeline/timeline.types";

interface SelectedMediaSidePanelContentProps {
  isStartTimeReadOnly: boolean;
  format: MediaFormatWithAdBreak | null;
  interstitialAdbreaks?: InterstitialAdbreaks;
  startTime: string;
  endTime: string;
  scheduleId: string;
  uuid: string;
  picture?: string | null;
}

function SelectedMediaSidePanelContent({
  isStartTimeReadOnly,
  format,
  interstitialAdbreaks,
  startTime,
  endTime,
  scheduleId,
  uuid,
  picture,
}: SelectedMediaSidePanelContentProps): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");

  const { state } = useFAST();

  const { schedules } = state;

  const getMediasMetadataListQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getMediasMetadataList,
    params: [],
  });

  const { data: mediasMetadata } = useQuery(getMediasMetadataListQuery);

  const isLive = moment().isBetween(startTime, endTime);

  const programPreviewTitle = isLive ? (
    <div className="flex items-center gap-[0.46875rem]">
      <div>{t("programPreview")}</div>
      <div className="text-muted-foreground text-[0.46875rem] font-normal">
        <Tag color="red" size="small">
          {t("isLive")}
        </Tag>
      </div>
    </div>
  ) : (
    t("programPreview")
  );

  const currentSchedule = schedules.find(
    (schedule) => schedule.uuid === scheduleId,
  );

  const isNotAdBreakFormat = format !== AD_BREAK;

  return (
    <React.Fragment>
      {scheduleId ? (
        <SidepanelBlock title={t("programInfo")}>
          <p className="font-medium">{t("associatedSchedule")}</p>
          <FastScheduleTag isFullWidth scheduleId={scheduleId} />
        </SidepanelBlock>
      ) : null}
      <SidepanelBlock title={t("programMetadata")}>
        {format ? (
          <React.Fragment>
            <p className="font-medium">{t("associatedMediaType")}</p>
            <FastFormatTag
              format={format}
              metadataFormats={mediasMetadata?.formats}
            />
          </React.Fragment>
        ) : null}

        <Input isCompact isReadOnly label={t("mediaTitle")} name="label" />
        <Input isCompact isReadOnly label={t("customId")} name="label" />
        <Input
          isCompact
          isReadOnly
          label={t("mediaDuration")}
          name="duration"
        />
        <div className="flex items-end justify-between gap-[0.46875rem] [&>*]:mb-0">
          <Input
            isCompact
            isReadOnly={isStartTimeReadOnly}
            label={t("startTime")}
            name="startTime"
          />
          <div className="border-primary bg-primary/10 flex size-[35px] shrink-0 items-center justify-center rounded-[0.46875rem] border p-[0.234375rem]">
            <Icon path={mdiArrowRight} size={0.9375} />
          </div>
          <Input isCompact isReadOnly label={t("endTime")} name="endTime" />
        </div>
      </SidepanelBlock>
      {isNotAdBreakFormat ? (
        <SidepanelBlock title={programPreviewTitle}>
          <VideoOverview
            isLive={isLive}
            poster={picture ?? undefined}
            schedule={currentSchedule}
            uuid={uuid}
          />
          {interstitialAdbreaks?.adbreakId ? (
            <React.Fragment>
              <AdbreakTimeline
                endTime={endTime}
                interstitialAdbreaks={interstitialAdbreaks}
                isLive={isLive}
                startTime={startTime}
              />
              <AdbreakBlock
                interstitialAdbreaks={interstitialAdbreaks}
                isLive={isLive}
              />
            </React.Fragment>
          ) : null}
        </SidepanelBlock>
      ) : null}
    </React.Fragment>
  );
}

export default SelectedMediaSidePanelContent;
